import React, { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Users, 
  Plus,
  Home,
  GraduationCap,
  Briefcase,
  MapPin,
  Search,
  Copy,
  Check,
  LogOut,
  Settings,
  Loader2,
  Crown,
  UserPlus,
  Lock,
  Globe,
  RefreshCw
} from "lucide-react";

const CIRCLE_ICONS = {
  quartier: Home,
  ecole: GraduationCap,
  travail: Briefcase
};

const CIRCLE_COLORS = {
  quartier: "bg-emerald-100 text-emerald-700 border-emerald-200",
  ecole: "bg-blue-100 text-blue-700 border-blue-200",
  travail: "bg-purple-100 text-purple-700 border-purple-200"
};

const CIRCLE_LABELS = {
  quartier: "Quartier",
  ecole: "École",
  travail: "Travail"
};

const CirclesPage = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [circles, setCircles] = useState([]);
  const [myCircles, setMyCircles] = useState([]);
  const [activeTab, setActiveTab] = useState("discover");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [selectedCircle, setSelectedCircle] = useState(null);
  const [joinCode, setJoinCode] = useState("");
  const [copiedCode, setCopiedCode] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  
  const [newCircle, setNewCircle] = useState({
    name: "",
    type: "quartier",
    description: "",
    location: "",
    is_private: false
  });

  const fetchCircles = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all circles
      const params = new URLSearchParams();
      if (filterType) params.append("type", filterType);
      if (searchQuery) params.append("search", searchQuery);
      
      const [allResponse, myResponse] = await Promise.all([
        axios.get(`${API}/circles?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/circles?my_circles=true`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setCircles(allResponse.data.circles);
      setMyCircles(myResponse.data.circles);
    } catch (error) {
      toast.error("Erreur lors du chargement des cercles");
    } finally {
      setLoading(false);
    }
  }, [token, filterType, searchQuery]);

  useEffect(() => {
    fetchCircles();
  }, [fetchCircles]);

  const handleCreateCircle = async () => {
    if (!newCircle.name.trim()) {
      toast.error("Le nom du cercle est requis");
      return;
    }
    
    setActionLoading("create");
    try {
      const response = await axios.post(`${API}/circles`, newCircle, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Cercle créé !");
      setShowCreateDialog(false);
      setNewCircle({ name: "", type: "quartier", description: "", location: "", is_private: false });
      fetchCircles();
      
      // Show the new circle
      setSelectedCircle(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setActionLoading(null);
    }
  };

  const handleJoinCircle = async (circleId) => {
    setActionLoading(circleId);
    try {
      await axios.post(`${API}/circles/${circleId}/join`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Vous avez rejoint le cercle !");
      fetchCircles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setActionLoading(null);
    }
  };

  const handleJoinByCode = async () => {
    if (!joinCode.trim()) {
      toast.error("Entrez un code d'invitation");
      return;
    }
    
    setActionLoading("join-code");
    try {
      const response = await axios.post(`${API}/circles/join-by-code?invite_code=${joinCode.toUpperCase()}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Vous avez rejoint "${response.data.circle_name}" !`);
      setShowJoinDialog(false);
      setJoinCode("");
      fetchCircles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Code invalide");
    } finally {
      setActionLoading(null);
    }
  };

  const handleLeaveCircle = async (circleId) => {
    setActionLoading(circleId);
    try {
      await axios.post(`${API}/circles/${circleId}/leave`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Vous avez quitté le cercle");
      setSelectedCircle(null);
      fetchCircles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setActionLoading(null);
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(true);
    toast.success("Code copié !");
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const handleRegenerateCode = async (circleId) => {
    try {
      const response = await axios.post(`${API}/circles/${circleId}/regenerate-code`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Nouveau code généré !");
      setSelectedCircle(prev => ({ ...prev, invite_code: response.data.invite_code }));
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const CircleCard = ({ circle, showJoinButton = true }) => {
    const Icon = CIRCLE_ICONS[circle.type] || Users;
    const colorClass = CIRCLE_COLORS[circle.type] || "bg-slate-100 text-slate-700";
    
    return (
      <Card 
        className="guardian-card hover:shadow-lg transition-all cursor-pointer"
        onClick={() => setSelectedCircle(circle)}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClass}`}>
              <Icon size={24} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-slate-900 truncate">{circle.name}</h3>
                {circle.is_private && <Lock size={14} className="text-slate-400" />}
              </div>
              <p className="text-sm text-slate-500 truncate">
                {circle.location || CIRCLE_LABELS[circle.type]}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="secondary" className="text-xs">
                  <Users size={12} className="mr-1" />
                  {circle.member_count} membre{circle.member_count > 1 ? "s" : ""}
                </Badge>
                {circle.is_member && (
                  <Badge className="bg-green-100 text-green-700 text-xs">
                    <Check size={12} className="mr-1" />
                    Membre
                  </Badge>
                )}
                {circle.is_admin && (
                  <Badge className="bg-amber-100 text-amber-700 text-xs">
                    <Crown size={12} className="mr-1" />
                    Admin
                  </Badge>
                )}
              </div>
            </div>
            {showJoinButton && !circle.is_member && !circle.is_private && (
              <Button
                size="sm"
                className="btn-guardian rounded-full"
                onClick={(e) => {
                  e.stopPropagation();
                  handleJoinCircle(circle.id);
                }}
                disabled={actionLoading === circle.id}
              >
                {actionLoading === circle.id ? (
                  <Loader2 className="animate-spin" size={16} />
                ) : (
                  <UserPlus size={16} />
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <Layout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Cercles de Confiance
            </h1>
            <p className="text-slate-500 text-sm">
              Rejoignez des communautés de quartier, école ou travail
            </p>
          </div>
          
          <div className="flex gap-2">
            <Dialog open={showJoinDialog} onOpenChange={setShowJoinDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full">
                  <UserPlus size={18} className="mr-2" />
                  Code
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Rejoindre avec un code</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label>Code d'invitation</Label>
                    <Input
                      placeholder="Ex: ABC123"
                      value={joinCode}
                      onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                      className="text-center text-xl font-mono tracking-widest rounded-xl"
                      maxLength={6}
                    />
                  </div>
                  <Button
                    className="w-full btn-guardian rounded-xl"
                    onClick={handleJoinByCode}
                    disabled={actionLoading === "join-code"}
                  >
                    {actionLoading === "join-code" ? (
                      <Loader2 className="animate-spin mr-2" size={18} />
                    ) : null}
                    Rejoindre
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="btn-guardian rounded-full">
                  <Plus size={18} className="mr-2" />
                  Créer
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Créer un cercle</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label className="font-bold">Type de cercle</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {Object.entries(CIRCLE_LABELS).map(([type, label]) => {
                        const Icon = CIRCLE_ICONS[type];
                        const isSelected = newCircle.type === type;
                        return (
                          <button
                            key={type}
                            type="button"
                            onClick={() => setNewCircle({ ...newCircle, type })}
                            className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${
                              isSelected 
                                ? CIRCLE_COLORS[type] + " border-current" 
                                : "border-slate-200 hover:border-slate-300"
                            }`}
                          >
                            <Icon size={24} />
                            <span className="text-xs font-medium">{label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="font-bold">Nom du cercle *</Label>
                    <Input
                      placeholder="Ex: Résidence Les Lilas"
                      value={newCircle.name}
                      onChange={(e) => setNewCircle({ ...newCircle, name: e.target.value })}
                      className="rounded-xl"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="font-bold">Lieu / Adresse</Label>
                    <Input
                      placeholder="Ex: 15 rue de la Paix, Lyon"
                      value={newCircle.location}
                      onChange={(e) => setNewCircle({ ...newCircle, location: e.target.value })}
                      className="rounded-xl"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="font-bold">Description</Label>
                    <Textarea
                      placeholder="Présentez votre cercle..."
                      value={newCircle.description}
                      onChange={(e) => setNewCircle({ ...newCircle, description: e.target.value })}
                      className="rounded-xl"
                      rows={2}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-2">
                      {newCircle.is_private ? <Lock size={18} /> : <Globe size={18} />}
                      <div>
                        <p className="font-medium text-sm">Cercle privé</p>
                        <p className="text-xs text-slate-500">Invitation requise</p>
                      </div>
                    </div>
                    <Switch
                      checked={newCircle.is_private}
                      onCheckedChange={(checked) => setNewCircle({ ...newCircle, is_private: checked })}
                    />
                  </div>
                  
                  <Button
                    className="w-full btn-guardian rounded-xl"
                    onClick={handleCreateCircle}
                    disabled={actionLoading === "create"}
                  >
                    {actionLoading === "create" ? (
                      <Loader2 className="animate-spin mr-2" size={18} />
                    ) : null}
                    Créer le cercle
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Search & Filter */}
        <Card className="guardian-card">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <Input
                  placeholder="Rechercher un cercle..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 rounded-xl"
                />
              </div>
              <Select value={filterType || "all"} onValueChange={(v) => setFilterType(v === "all" ? "" : v)}>
                <SelectTrigger className="w-full sm:w-40 rounded-xl">
                  <SelectValue placeholder="Tous types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous types</SelectItem>
                  <SelectItem value="quartier">Quartier</SelectItem>
                  <SelectItem value="ecole">École</SelectItem>
                  <SelectItem value="travail">Travail</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 rounded-xl">
            <TabsTrigger value="discover" className="rounded-xl">
              <Globe size={16} className="mr-2" />
              Découvrir
            </TabsTrigger>
            <TabsTrigger value="my-circles" className="rounded-xl">
              <Users size={16} className="mr-2" />
              Mes cercles ({myCircles.length})
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="discover" className="mt-4">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="animate-spin text-[#FF3366]" size={40} />
              </div>
            ) : circles.length > 0 ? (
              <div className="grid gap-3">
                {circles.map((circle) => (
                  <CircleCard key={circle.id} circle={circle} />
                ))}
              </div>
            ) : (
              <Card className="guardian-card">
                <CardContent className="p-8 text-center">
                  <Users size={48} className="mx-auto text-slate-300 mb-4" />
                  <h3 className="font-bold text-slate-900 mb-2">Aucun cercle trouvé</h3>
                  <p className="text-slate-500 text-sm mb-4">
                    Créez le premier cercle de votre communauté !
                  </p>
                  <Button onClick={() => setShowCreateDialog(true)} className="btn-guardian rounded-full">
                    <Plus size={18} className="mr-2" />
                    Créer un cercle
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          <TabsContent value="my-circles" className="mt-4">
            {myCircles.length > 0 ? (
              <div className="grid gap-3">
                {myCircles.map((circle) => (
                  <CircleCard key={circle.id} circle={circle} showJoinButton={false} />
                ))}
              </div>
            ) : (
              <Card className="guardian-card">
                <CardContent className="p-8 text-center">
                  <Users size={48} className="mx-auto text-slate-300 mb-4" />
                  <h3 className="font-bold text-slate-900 mb-2">Aucun cercle rejoint</h3>
                  <p className="text-slate-500 text-sm">
                    Rejoignez des cercles pour faire partie de communautés de confiance.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Circle Detail Dialog */}
        {selectedCircle && (
          <Dialog open={!!selectedCircle} onOpenChange={() => setSelectedCircle(null)}>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <div className="flex items-center gap-3">
                  {(() => {
                    const Icon = CIRCLE_ICONS[selectedCircle.type] || Users;
                    return (
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${CIRCLE_COLORS[selectedCircle.type]}`}>
                        <Icon size={24} />
                      </div>
                    );
                  })()}
                  <div>
                    <DialogTitle className="flex items-center gap-2">
                      {selectedCircle.name}
                      {selectedCircle.is_private && <Lock size={16} className="text-slate-400" />}
                    </DialogTitle>
                    <p className="text-sm text-slate-500">
                      {selectedCircle.location || CIRCLE_LABELS[selectedCircle.type]}
                    </p>
                  </div>
                </div>
              </DialogHeader>
              
              <div className="space-y-4 pt-2">
                {selectedCircle.description && (
                  <p className="text-slate-600 text-sm">{selectedCircle.description}</p>
                )}
                
                {/* Invite Code (Admin only) */}
                {selectedCircle.is_admin && selectedCircle.invite_code && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-amber-800">Code d'invitation</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRegenerateCode(selectedCircle.id)}
                        className="h-7 text-xs"
                      >
                        <RefreshCw size={12} className="mr-1" />
                        Régénérer
                      </Button>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-2xl font-mono font-bold text-amber-900 tracking-widest text-center">
                        {selectedCircle.invite_code}
                      </code>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCopyCode(selectedCircle.invite_code)}
                      >
                        {copiedCode ? <Check size={16} /> : <Copy size={16} />}
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Members */}
                <div>
                  <h4 className="font-bold text-slate-900 mb-3">
                    Membres ({selectedCircle.member_count})
                  </h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {selectedCircle.members?.map((member) => (
                      <div key={member.user_id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-200 to-pink-100 flex items-center justify-center text-sm font-bold text-[#FF3366]">
                          {member.full_name?.charAt(0) || "?"}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm text-slate-900">{member.full_name}</p>
                          <p className="text-xs text-slate-500 capitalize">{member.level}</p>
                        </div>
                        {member.role === "admin" && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">
                            <Crown size={10} className="mr-1" />
                            Admin
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  {selectedCircle.is_member ? (
                    <>
                      {!selectedCircle.is_admin && (
                        <Button
                          variant="outline"
                          className="flex-1 rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => handleLeaveCircle(selectedCircle.id)}
                          disabled={actionLoading === selectedCircle.id}
                        >
                          {actionLoading === selectedCircle.id ? (
                            <Loader2 className="animate-spin mr-2" size={16} />
                          ) : (
                            <LogOut size={16} className="mr-2" />
                          )}
                          Quitter
                        </Button>
                      )}
                    </>
                  ) : !selectedCircle.is_private ? (
                    <Button
                      className="flex-1 btn-guardian rounded-xl"
                      onClick={() => handleJoinCircle(selectedCircle.id)}
                      disabled={actionLoading === selectedCircle.id}
                    >
                      {actionLoading === selectedCircle.id ? (
                        <Loader2 className="animate-spin mr-2" size={16} />
                      ) : (
                        <UserPlus size={16} className="mr-2" />
                      )}
                      Rejoindre
                    </Button>
                  ) : (
                    <div className="flex-1 p-3 bg-slate-100 rounded-xl text-center text-sm text-slate-600">
                      <Lock size={16} className="inline mr-2" />
                      Cercle privé - Code d'invitation requis
                    </div>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
};

export default CirclesPage;
